'use client';

/* eslint-disable consistent-return */
import { startTransition, useEffect, useLayoutEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useStableCallback } from '../../../hooks/useStableCallback';

export type RectSize = { width: number; height: number };

export type SetSize = (size: RectSize) => void;

/**
 * Provide the size of the element to the `setSize` function.
 *
 * @example
 * const ref = useRef(null);
 *
 * useSize(ref, ({ width, height }) => {
 *   // do something with the size
 * })
 */

export function useSize(
  ref: React.RefObject<HTMLElement | null>,
  setSize: SetSize,
  { enabled = true, flushSyncUpdates = false } = {}
) {
  const lastSizeRef = useRef<RectSize | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;

    if (!el) {
      throw new Error('useSize: ref is not attached to an element');
    }

    if (!lastSizeRef.current && enabled) {
      const rect = el.getBoundingClientRect();
      const newSize = {
        height: rect.height,
        width: rect.width,
      };

      lastSizeRef.current = newSize;
      setSize(newSize);
    }
  });

  const stableSetSize = useStableCallback(setSize);

  useEffect(() => {
    if (enabled) {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        const box = entry.borderBoxSize[0];

        const newSize = {
          height: box.blockSize,
          width: box.inlineSize,
        };

        if (
          lastSizeRef.current &&
          Math.abs(newSize.width - lastSizeRef.current.width) < 0.5 &&
          Math.abs(newSize.height - lastSizeRef.current.height) < 0.5
        ) {
          return;
        }

        lastSizeRef.current = newSize;

        if (flushSyncUpdates) {
          flushSync(() => {
            stableSetSize(newSize);
          });
        } else {
          startTransition(() => {
            stableSetSize(newSize);
          });
        }
      });

      observer.observe(ref.current!, { box: 'border-box' });
      return () => observer.disconnect();
    }
  }, [enabled, flushSyncUpdates, ref, stableSetSize]);
}
